import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GetVerificationResponse } from '../../models/intefaces/get-verification-response';
import { PushChannelFactory } from '../../modules/push-notifications/src/models/push-channel-factory';
import { PUSH_CHANNEL_FACTORY } from '../../modules/push-notifications/src/models/injector';
import { PushChannel } from '../../modules/push-notifications/src/models/push-channel';
import { PushEvent } from '../../modules/push-notifications';
import { GetBroadcastData } from '../../models/intefaces/get-broadcast-data';
import { FileType, getFileName } from '../../models/enums/file-type';
import {
  Target,
  VerificationService,
} from '../../services/verification/verification.service';
import { CanvasComponent } from '../canvas/canvas.component';
import { OrderingProcessService } from '../../services/ordering-process/ordering-process.service';
import { StepImpl } from '../../models/steps/step-impl';
import { FormFileInput } from '../../models/form-file-input';
import { FileReaderService } from '../../services/file-reader/file-reader.service';
import { TranslationsService } from '../../services/translations/translations.service';
import { OrderService } from 'src/app/services/order/order.service';
import resizeImage from 'src/app/services/image-resize';

const ChannelName = 'kala-website';

@Component({
  selector: 'app-verification',
  templateUrl: './verification.component.html',
  styleUrls: ['./verification.component.scss'],
})
export class VerificationComponent implements OnInit {
  url = '';

  public generatedLink = '';
  public isGenerateDocumentLoading = false;
  public isMobile = false;

  readonly front = FileType.front;
  readonly back = FileType.back;
  readonly signature = FileType.signature;

  private qrCode = '';
  private csrfToken = '';
  private token = '';

  private channel?: PushChannel;

  public broadcast?: GetBroadcastData;

  private step?: StepImpl;

  @ViewChild(CanvasComponent)
  canvas?: CanvasComponent;

  public loadingFromQrCode: boolean = false;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly verificationService: VerificationService,
    private readonly fileReader: FileReaderService,
    private readonly translationsService: TranslationsService,
    @Inject(PUSH_CHANNEL_FACTORY)
    private readonly pushChannelFactory: PushChannelFactory,
    private readonly orderingProcessService: OrderingProcessService,
    private readonly orderService: OrderService
  ) {}

  showDocument(): void {
    
    const exported = this.orderService.export();

    this.verificationService.fill(exported, [
      'signature',
      'card_recto',
      'card_verso',
    ]);

    let postParams: any = {
      ...exported,
      signature: sessionStorage.getItem('signature') || null,
      partner_token: this.verificationService.getPartnerToken(),
    };

    let responseType: any = {
      responseType: 'text' as 'json',
    }

    const pdfUrl = `${this.translationsService.getTranslations().api_url}/api/v1/order/generateTemporaryDocuments`;


    this.isGenerateDocumentLoading = true;

    this.httpClient
      .post<string>(
        pdfUrl,
        postParams,
        responseType
      )
      .subscribe(
        (response) => {
          this.isGenerateDocumentLoading = false;

          const url = `${this.translationsService.getTranslations().api_url}/api/v1/order/readTemporaryDocument/${response}`;

          window.open(url, '_blank')

          this.generatedLink = url;
      }, 
      (error) => {
        this.isGenerateDocumentLoading = false;
        alert("Something went wrong...")
      });
  }

  ngOnInit(): void {
    this.step = this.orderingProcessService.getCurrentStep();
    this.checkReady();

    if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)){
      this.isMobile = true
    }

    this.repopulateImages();
    setTimeout(this.loadInitialCanvas, 1000);

    this.httpClient
      .get<GetVerificationResponse>(
        `${this.translationsService.getCurrentLanguage()}/form/getVerification`
      )
      .subscribe((response) => this.onResponse(response));
  }

  private async repopulateImages() {
    this.populateImage('card_recto');
    this.populateImage('card_verso');
    this.checkReady();
  }

  private loadInitialCanvas = () => {
    const signature = sessionStorage.getItem('signature');

    if (signature) {
      const signatureBase64 = this.getBase64(signature);

      this.canvas?.loadDrawing(signatureBase64);

      this.verificationService.addSignature(signature);

      this.checkReady();
    }
  };

  private async populateImage(name: 'card_recto' | 'card_verso') {
    const card = sessionStorage.getItem(name);
    const signature = sessionStorage.getItem('signature');

    this.broadcast = this.broadcast || {
      card_recto: '',
      card_verso: '',
      signature: signature || '',
    };

    if (card) {
      const base64 = `data:image/jpeg;base64,${card}`;
      const base64Response = await fetch(base64);
      this.broadcast[name] = base64;

      const blob = await base64Response.blob();
      const file = new File([blob], `${name}.jpg`, {
        type: blob.type,
      });
      const formFile = new FormFileInput(file, getFileName(0));
      formFile.setData(base64.toString());
      this.setBroadcast(name === "card_recto" ? FileType.front : FileType.back, formFile);

      const image = (formFile.getData() as string).split(',')[1] || '';
      this.verificationService.addValue(name, image);
    }
  }

  private checkReady(): void {
    const required = ['card_recto', 'card_verso', 'signature'];
    if (this.verificationService.hasValues(required)) {
      this.step?.setReady();
    } else {
      this.step?.reset();
    }
  }

  onFileReady(file: File, type: FileType): void {
    const typeName: string = type == 0 ? 'card_recto' : 'card_verso';


    const formFile = new FormFileInput(file, getFileName(type));

    formFile.readAsBase64(this.fileReader).then(() => {
      this.setBroadcast(type, formFile);
      
      const image = (formFile.getData() as string).split(',')[1] || '';
      
      const imageFile = new Image();
      imageFile.src = this.getBase64(image);
      imageFile.onload = () => {
        const data = resizeImage(imageFile);
        const imageRemoveBase64String = (data as string).split(',')[1] || '';

        sessionStorage.setItem(typeName, imageRemoveBase64String);

        this.verificationService.addValue(formFile.getInputName(), imageRemoveBase64String);

        this.checkReady();
      }
    });
  }

  private setBroadcast(type: FileType, formFile: FormFileInput): void {
    const signature = sessionStorage.getItem('signature');
    this.broadcast = this.broadcast || {
      card_recto: '',
      card_verso: '',
      signature: signature || '',
    };
    const value = formFile.getData() as string;
    if (type === FileType.front) {
      this.broadcast.card_recto = value;
    } else {
      this.broadcast.card_verso = value;
    }
  }

  onDrawEnd(): void {
    const image = this.canvas?.getDrawing() || '';
    const drawinData = image.split(',')[1] || '';
    this.verificationService.addSignature(drawinData);
    sessionStorage.setItem('signature', drawinData);
    this.checkReady();
  }

  send(): void {
    this.verificationService.send(Target.web).subscribe();
  }

  getQrCode(): string {
    return this.qrCode;
  }

  private onResponse(response: GetVerificationResponse): void {
    this.url = response.url;
    this.qrCode = response.qrCode;
    this.csrfToken = response.session;
    this.token = response.token;
    this.channel = this.pushChannelFactory.createChannel(ChannelName);
    this.channel
      ?.getEvent(`broadcast-${response.token}`)
      .subscribe((event: PushEvent) => this.onPushEvent(event));
  }

  private onPushEvent(event: PushEvent): void {
    const data = {
      _token: this.csrfToken,
      token: this.token,
    };

    this.loadingFromQrCode = true;

    this.httpClient
      .post<GetBroadcastData>('request/getBroadcastData', data)
      .subscribe((response) => {
        this.onBroadcastData(response)
        this.loadingFromQrCode = false;
      });
  }

  getBase64(value: string): string {
    return `data:image/png;base64,${value}`;
  }

  reset(): void {
    if (this.canvas instanceof CanvasComponent) {
      this.canvas.reset();
      this.verificationService.remove('signature');
      sessionStorage.removeItem('signature');
      this.checkReady();
    }
  }

  private onBroadcastData(response: GetBroadcastData): void {
    this.reset();
    
    this.broadcast = response;
    
    const cardVerso = response.card_verso.split(',')[1] || '';
    const cardRecto = response.card_recto.split(',')[1] || '';
    const signature = response.signature.split(',')[1] || '';
    this.verificationService.addValue('card_recto', cardRecto);
    this.verificationService.addValue('card_verso', cardVerso);
    this.verificationService.addSignature(signature);
    this.canvas?.loadDrawing(response.signature);
    this.checkReady();
    
    try {
      sessionStorage.setItem('card_verso', cardVerso)
      sessionStorage.setItem('card_recto', cardRecto);
      sessionStorage.setItem('signature', signature);  
    }
    catch(exception) 
    {
      console.log({ exception })
    }
  }
}
